import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../lib/api'
import type {
  AdminUser,
  AdminChurch,
  AdminChurchDetail,
  AdminEvent,
  AuditLogEntry,
  AdminAutoScrapeResult,
  AdminScrapeResult,
  AdminWaitlistEntry,
  AdminChurchRequest,
} from '../types/admin'

// ─── Users ────────────────────────────────────────────────────────────────────

export function useAdminUsers(params: { q?: string; page: number; showDeleted?: boolean }) {
  const { q, page, showDeleted } = params
  const search = new URLSearchParams({ page: String(page), limit: '20' })
  if (q) search.set('q', q)
  if (showDeleted) search.set('showDeleted', 'true')
  return useQuery({
    queryKey: ['admin', 'users', page, q, showDeleted],
    queryFn: () =>
      apiFetch<{ data: AdminUser[]; meta: { total: number; page: number; limit: number } }>(
        `/admin/users?${search}`,
      ),
  })
}

export function useAdminCreateUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      email: string
      password: string
      displayName: string
      role: string
    }) => apiFetch<{ data: AdminUser }>('/admin/users', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

export function useAdminUpdateUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string
      role?: string
      displayName?: string
      email?: string
      password?: string
    }) => apiFetch(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

export function useAdminDeleteUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/admin/users/${id}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

export function useAdminRestoreUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/admin/users/${id}/restore`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

// ─── Churches ─────────────────────────────────────────────────────────────────

export function useAdminChurches(params: {
  page: number
  showDeleted?: boolean
  q?: string
  denomination?: string
  sort?: string
  sortDir?: 'asc' | 'desc'
}) {
  const { page, showDeleted, q, denomination, sort, sortDir } = params
  const search = new URLSearchParams({ page: String(page), limit: '20' })
  if (showDeleted) search.set('showDeleted', 'true')
  if (q) search.set('q', q)
  if (denomination) search.set('denomination', denomination)
  if (sort) search.set('sort', sort)
  if (sortDir) search.set('sortDir', sortDir)
  return useQuery({
    queryKey: ['admin', 'churches', page, showDeleted, q, denomination, sort, sortDir],
    queryFn: () =>
      apiFetch<{ data: AdminChurch[]; meta: { total: number; page: number; limit: number } }>(
        `/admin/churches?${search}`,
      ),
  })
}

export function useAdminCreateChurch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<{ data: AdminChurchDetail }>('/admin/churches', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'churches'] }),
  })
}

export function useAdminUpdateChurch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      apiFetch(`/admin/churches/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: (_d, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches'] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches', id] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches', 'scraped'] })
    },
  })
}

export function useAdminToggleVerified() {
  const update = useAdminUpdateChurch()
  return {
    mutate: (args: { id: string; verified: boolean }) =>
      update.mutate({ id: args.id, verified: args.verified }),
    isPending: update.isPending,
  }
}

export function useAdminDeleteChurch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/admin/churches/${id}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'churches'] }),
  })
}

export function useAdminRestoreChurch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/admin/churches/${id}/restore`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'churches'] }),
  })
}

export function useAdminChurchDetail(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'churches', id],
    queryFn: () => apiFetch<{ data: AdminChurchDetail }>(`/admin/churches/${id}`),
    enabled: Boolean(id),
  })
}

export function useAdminAddChurchMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      churchId,
      userId,
      role,
    }: {
      churchId: string
      userId: string
      role: string
    }) =>
      apiFetch(`/admin/churches/${churchId}/admins`, {
        method: 'POST',
        body: JSON.stringify({ userId, role }),
      }),
    onSuccess: (_d, { churchId }) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches', churchId] })
    },
  })
}

export function useAdminRemoveChurchMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ churchId, userId }: { churchId: string; userId: string }) =>
      apiFetch(`/admin/churches/${churchId}/admins/${userId}`, { method: 'DELETE' }),
    onSuccess: (_d, { churchId }) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches', churchId] })
    },
  })
}

export function useAdminUpdateChurchMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ churchId, userId, role }: { churchId: string; userId: string; role: string }) =>
      apiFetch(`/admin/churches/${churchId}/admins/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }),
    onSuccess: (_data, { churchId }) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches', churchId] })
    },
  })
}

// ─── Events ───────────────────────────────────────────────────────────────────

export function useAdminEvents(params: {
  page: number
  showDeleted?: boolean
  q?: string
  eventType?: string
  status?: string
  source?: string
  sort?: string
  sortDir?: 'asc' | 'desc'
}) {
  const { page, showDeleted, q, eventType, status, source, sort, sortDir } = params
  const search = new URLSearchParams({ page: String(page), limit: '20' })
  if (showDeleted) search.set('showDeleted', 'true')
  if (q) search.set('q', q)
  if (eventType) search.set('eventType', eventType)
  if (status) search.set('status', status)
  if (source) search.set('source', source)
  if (sort) search.set('sort', sort)
  if (sortDir) search.set('sortDir', sortDir)
  return useQuery({
    queryKey: ['admin', 'events', page, showDeleted, q, eventType, status, source, sort, sortDir],
    queryFn: () =>
      apiFetch<{ data: AdminEvent[]; meta: { total: number; page: number; limit: number } }>(
        `/admin/events?${search}`,
      ),
  })
}

export function useAdminEvent(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'events', 'detail', id],
    queryFn: () => apiFetch<{ data: AdminEvent }>(`/admin/events/${id}`),
    enabled: Boolean(id),
  })
}

export function useAdminCreateEvent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<{ data: AdminEvent }>('/admin/events', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'events'] }),
  })
}

export interface ImportEventItem {
  title: string
  eventType: string
  description?: string
  addressText?: string
  startsAt: string
  endsAt?: string
}

export function useAdminImportEvents() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ churchId, events }: { churchId: string; events: ImportEventItem[] }) =>
      apiFetch<{ data: { imported: number; total: number; errors: string[] } }>('/admin/events/import', {
        method: 'POST',
        body: JSON.stringify({ churchId, events }),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'events'] }),
  })
}

export function useAdminUpdateEvent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      apiFetch(`/admin/events/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'events'] }),
  })
}

export function useAdminDeleteEvent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/admin/events/${id}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'events'] }),
  })
}

export function useAdminRestoreEvent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/admin/events/${id}/restore`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'events'] }),
  })
}

// ─── Outreach ─────────────────────────────────────────────────────────────────

export function useAdminChurchOutreach() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (churchId: string) =>
      apiFetch(`/admin/churches/${churchId}/outreach`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'audit-log'] }),
  })
}

// ─── Claim requests ───────────────────────────────────────────────────────────

export interface AdminClaimRequest {
  id: string
  churchId: string
  userId: string
  status: 'pending' | 'approved' | 'denied'
  autoApproved: boolean
  createdAt: string
  churchName?: string
  churchEmail?: string | null
  churchWebsiteUrl?: string | null
  userEmail?: string
  userDisplayName?: string
}

export function useAdminClaimRequests() {
  return useQuery({
    queryKey: ['admin', 'claim-requests'],
    queryFn: () => apiFetch<{ data: AdminClaimRequest[] }>('/admin/claim-requests'),
  })
}

export function useAdminApproveClaimRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (requestId: string) =>
      apiFetch(`/admin/claim-requests/${requestId}/approve`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'claim-requests'] }),
  })
}

export function useAdminDenyClaimRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (requestId: string) =>
      apiFetch(`/admin/claim-requests/${requestId}/deny`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'claim-requests'] }),
  })
}

// ─── Review queue (scraped items) ─────────────────────────────────────────────

export function useAdminScrapedChurches() {
  return useQuery({
    queryKey: ['admin', 'churches', 'scraped'],
    queryFn: () =>
      apiFetch<{ data: AdminChurch[]; meta: { total: number; page: number; limit: number } }>(
        `/admin/churches?page=1&limit=100`,
      ),
    select: (res) => ({
      ...res,
      data: res.data.filter((c) => !c.claimed && !c.verified),
    }),
  })
}

export function useAdminScrapedEvents() {
  return useQuery({
    queryKey: ['admin', 'events', 'scraped'],
    queryFn: () =>
      apiFetch<{ data: AdminEvent[]; meta: { total: number; page: number; limit: number } }>(
        `/admin/events?page=1&limit=100`,
      ),
    select: (res) => ({
      ...res,
      data: res.data.filter((e) => e.source === 'scraped'),
    }),
  })
}

// ─── Coverage map ─────────────────────────────────────────────────────────────

export interface CoverageChurch {
  id: string
  name: string
  lat: number
  lng: number
  status: 'claimed' | 'active' | 'stale' | 'discovered' | 'bare'
}

export function useAdminCoverage() {
  return useQuery({
    queryKey: ['admin', 'coverage'],
    queryFn: () => apiFetch<{ data: CoverageChurch[] }>('/admin/coverage'),
    staleTime: 60_000,
  })
}

export interface DiscoverCandidate {
  osmId: number
  osmType: string
  name: string
  denomination: string | null
  address: string | null
  lat: number
  lng: number
  phone: string | null
  websiteUrl: string | null
  alreadyExists: boolean
}

export interface ImportCandidate {
  name: string
  denomination: string | null
  address: string | null
  lat: number
  lng: number
  phone: string | null
  websiteUrl: string | null
}

export function useAdminDiscoverPreview() {
  return useMutation({
    mutationFn: (body: { lat: number; lng: number; radiusKm: number; denominations?: string[] }) =>
      apiFetch<{ data: DiscoverCandidate[] }>('/admin/discover/preview', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  })
}

export interface EnrichResult {
  name: string
  denomination: string | null
  address: string | null
}

export function useAdminDiscoverEnrich() {
  return useMutation({
    mutationFn: (candidates: Pick<ImportCandidate, 'name' | 'denomination' | 'address' | 'lat' | 'lng'>[]) =>
      apiFetch<{ data: EnrichResult[] }>('/admin/discover/enrich', {
        method: 'POST',
        body: JSON.stringify({ candidates }),
      }),
  })
}

export function useAdminDiscoverImport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (candidates: ImportCandidate[]) =>
      apiFetch<{ data: { imported: number; total: number } }>('/admin/discover/import', {
        method: 'POST',
        body: JSON.stringify({ candidates }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'coverage'] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches'] })
    },
  })
}

// ─── Scrape ───────────────────────────────────────────────────────────────────

export function useAdminAutoScrape() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { churchId: string }) =>
      apiFetch<{ data: AdminAutoScrapeResult }>('/admin/scrape/auto', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches'] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'events'] })
    },
  })
}

export function useAdminScrape() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { sourceUrl: string; churchId?: string }) =>
      apiFetch<{ data: AdminScrapeResult }>('/admin/scrape', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'churches'] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'events'] })
    },
  })
}

// ─── Waitlist ─────────────────────────────────────────────────────────────────

export function useAdminWaitlist(page: number) {
  const search = new URLSearchParams({ page: String(page), limit: '30' })
  return useQuery({
    queryKey: ['admin', 'waitlist', page],
    queryFn: () =>
      apiFetch<{ data: AdminWaitlistEntry[]; meta: { total: number; page: number; limit: number } }>(
        `/admin/waitlist?${search}`,
      ),
  })
}

// ─── Church requests ──────────────────────────────────────────────────────────

export function useAdminChurchRequests(page: number) {
  const search = new URLSearchParams({ page: String(page), limit: '30' })
  return useQuery({
    queryKey: ['admin', 'church-requests', page],
    queryFn: () =>
      apiFetch<{ data: AdminChurchRequest[]; meta: { total: number; page: number; limit: number } }>(
        `/admin/church-requests?${search}`,
      ),
  })
}

// ─── Audit log ────────────────────────────────────────────────────────────────

export function useAdminAuditLog(params: { targetType?: string; page: number }) {
  const { targetType, page } = params
  const search = new URLSearchParams({ page: String(page), limit: '30' })
  if (targetType) search.set('targetType', targetType)
  return useQuery({
    queryKey: ['admin', 'audit-log', page, targetType],
    queryFn: () =>
      apiFetch<{ data: AuditLogEntry[]; meta: { total: number; page: number; limit: number } }>(
        `/admin/audit-log?${search}`,
      ),
  })
}
